import { useState } from 'react';
import { Share2, Check, X, Copy, MessageCircle, Twitter, Facebook, Mail } from 'lucide-react';

/**
 * Share button + popover for a song page. Generates rich, ready-to-copy text
 * for WhatsApp / Twitter / Facebook + a clipboard action. Uses the Web Share
 * API when available (mobile) for a native share sheet.
 */
export default function ShareSongButton({ song }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = typeof window !== 'undefined' ? `${window.location.origin}/song/${song.id}` : `/song/${song.id}`;
  const text = `🎸 J'apprends "${song.title}" de ${song.artist} sur Maestro — accords synchronisés avec la vidéo, à mon rythme.`;

  const openNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Maestro · ${song.title}`, text, url });
        return;
      } catch { /* user cancelled or unsupported → fall back to popover */ }
    }
    setOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const wa = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${url}`)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
  const mail = `mailto:?subject=${encodeURIComponent(`Maestro · ${song.title}`)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;

  return (
    <div className="relative inline-block">
      <button
        data-testid="share-song-btn"
        onClick={openNative}
        className="btn-ghost inline-flex items-center gap-2 text-sm"
        title="Partager cette chanson"
      >
        <Share2 size={16} /> Partager
      </button>
      {open && (
        <>
          <button
            aria-label="Fermer"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-transparent"
            data-testid="share-song-backdrop"
          />
          <div
            className="absolute right-0 mt-2 w-72 z-50 rounded-2xl border border-stone-700 bg-stone-900 shadow-2xl shadow-black/60 p-3"
            data-testid="share-song-popover"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-stone-400">Partager</div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-100"><X size={14} /></button>
            </div>
            <div className="text-xs text-stone-300 mb-3 leading-snug bg-stone-950/50 border border-stone-800 rounded-lg p-2">
              {text}
              <div className="text-stone-500 mt-1 truncate">{url}</div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-stone-800 text-xs text-stone-300">
                <MessageCircle size={18} className="text-emerald-400" /> WhatsApp
              </a>
              <a href={tw} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-stone-800 text-xs text-stone-300">
                <Twitter size={18} className="text-sky-400" /> Twitter
              </a>
              <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-stone-800 text-xs text-stone-300">
                <Facebook size={18} className="text-blue-400" /> Facebook
              </a>
              <a href={mail} data-testid="share-mail" className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-stone-800 text-xs text-stone-300">
                <Mail size={18} className="text-orange-300" /> Email
              </a>
            </div>
            <button
              onClick={copyLink}
              data-testid="share-copy-link"
              className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-colors ${
                copied ? 'bg-emerald-900/60 text-emerald-200 border border-emerald-800/70' : 'bg-orange-700 hover:bg-orange-600 text-white'
              }`}
            >
              {copied ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Copier le lien</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
